/// <amd-module name="TFS/Admin/Groups/Common/Components/FilterablePivot" />
import * as React from "react";

import * as Resources from "TFS/Admin/Groups/Common/Resources";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { ISearchResultCache } from "TFS/Admin/Groups/Common/Contracts";

import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageContext } from "VSS/Platform/Context";
import { localeFormat } from "VSS/Core/Util/String";

import { Table, ITableColumn } from "VSSUI/Table";
import { Card } from "VSSUI/Card";
import { ITableRowDetails } from "VSSUI/Components/Table/Table.Props";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { FILTER_APPLIED_EVENT, FILTER_RESET_EVENT, IFilterState, IFilter } from "VSSUI/Utilities/Filter";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IFilterablePivotProps {
    filter: IFilter;
    pageContext: IVssPageContext;
}

export interface IFilterablePivotOwnProps {
    searchResult: ISearchResultCache;
}

export abstract class FilterablePivot<TProps extends IFilterablePivotProps & IFilterablePivotOwnProps> extends React.Component<TProps> {
    protected abstract _isContentReady(): boolean;

    protected abstract _renderContent(): JSX.Element;

    protected abstract _onFilterChanged(keyword: string): void;

    protected abstract _getFilteredContentColumns(): ITableColumn<any>[];

    protected abstract _getItemsName(): string;

    protected abstract _onRenderRow?(index: number, item: any, details: ITableRowDetails<any>): JSX.Element;

    protected abstract _isProjectScope(): boolean;

    protected abstract _isCollectionNavigationAllowed(): boolean;

    protected abstract _onExpandSearch(): void;

    protected _isFiltering(): boolean {
        const { searchResult } = this.props;
        return searchResult != null && searchResult.status === Constants.SearchStatus.Searching;
    }

    protected _isFilteredContentReady(): boolean {
        const { searchResult } = this.props;
        return searchResult && searchResult.status !== Constants.SearchStatus.None && searchResult.status !== Constants.SearchStatus.Searching;
    }

    protected _componentWillUnmount() {}

    protected _componentDidMount() {}

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        return resultItems.length > 0 ? (
            <Card>
                <Table<any> itemProvider={new ArrayItemProvider<any>(resultItems)} columns={this._getFilteredContentColumns()} />
            </Card>
        ) : (
            this._getZeroData()
        );
    }

    protected _getZeroData(): JSX.Element {
        return (
            <Card className="flex-grow bolt-card-no-vertical-padding permissions-minimum-card-height" contentProps={{ contentPadding: false }}>
                <div className="permissions-zero-data">
                    <ZeroData
                        imagePath={getAssetLocation(
                            this.props.pageContext,
                            "ms.vss-tfs-web/platform-content/Illustrations/general-no-results-found.svg"
                        )}
                        imageAltText={localeFormat(Resources.FilterZeroDataMessage, this._getItemsName())}
                        primaryText={localeFormat(Resources.FilterZeroDataMessage, this._getItemsName())}
                        secondaryText={
                            this._showExpandedSearch() ? (
                                <span>{localeFormat(Resources.FilterZeroDataSecondaryMessage, this._getItemsName())}</span>
                            ) : undefined
                        }
                        actionText={this._showExpandedSearch() ? Resources.ExpandSearchButtonText : undefined}
                        actionType={ZeroDataActionType.ctaButton}
                        onActionClick={(event, item) => this._onExpandSearch()}
                    />
                </div>
            </Card>
        );
    }

    private _showExpandedSearch(): boolean {
        return this._isProjectScope() && this._isCollectionNavigationAllowed();
    }

    protected _renderSpinner(): JSX.Element {
        return (
            <div className="flex-row flex-center justify-center flex-grow permissions-minimum-card-height">
                <Spinner size={SpinnerSize.large} label={Resources.Loading} />
            </div>
        );
    }

    public render(): JSX.Element {
        return this._isContentReady()
            ? this._isFilteredContentReady()
                ? this._renderFilteredContent()
                : this._renderContent()
            : this._renderSpinner();
    }

    private _onFilterApplied = (filterState: IFilterState, action?: string): void => {
        const keywordFilterItemState = filterState[Constants.SearchKeyword];
        const keyword = filterState && keywordFilterItemState && keywordFilterItemState.value;
        this._onFilterChanged(keyword);
    };

    private _onFilterReset = (): void => {
        this._onFilterChanged("");
    };

    public componentDidMount() {
        // Try filter with applied state after component is mounted.
        const appliedState = this.props.filter.getAppliedState();
        this._onFilterApplied(appliedState);
        this.props.filter.subscribe(this._onFilterApplied, FILTER_APPLIED_EVENT);
        this.props.filter.subscribe(this._onFilterReset, FILTER_RESET_EVENT);
        this.props.filter.reset();
        this._componentDidMount();
    }

    public componentWillUnmount() {
        this.props.filter.unsubscribe(this._onFilterReset, FILTER_RESET_EVENT);
        this.props.filter.unsubscribe(this._onFilterApplied, FILTER_APPLIED_EVENT);
        this._componentWillUnmount();
    }
}
